/**
 * ExamApplicationModal — os dados de um exame aplicado (#606, E6): obrigatório, periodicidade e
 * observações, com a completude no topo. Usada também pela Migração de Riscos e Exames, que não
 * passa `destino`: aí o título continua o antigo ("Aplicar Exame" / "Editar Aplicação").
 *
 * @version 2025.1.0
 */

import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  ApplicationData,
  AvailableExam,
  ExamWithApplication,
  isExamWithApplication,
} from '../../types/examApplication';
import type { NivelDeAplicacao } from '../../types/nivelDeAplicacao';
import { parseFrequencyToMonths } from '../../utils/examFrequency';
import { AcaoEmLink } from '../applications/AcaoEmLink';
import { BarraDeCompletude } from '../applications/BarraDeCompletude';
import { referencia } from '../risk-applications/textosDoDestino';

// ============================================================================
// TYPES
// ============================================================================

export interface ExamApplicationModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when form is submitted */
  onConfirm: (data: ApplicationData) => Promise<void>;
  /** The exam being applied/edited */
  exam: AvailableExam | ExamWithApplication | null;
  /** Mode: 'apply' for new application, 'edit' for existing */
  mode: 'apply' | 'edit';
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** O destino da aplicação: com ele, o título diz "Dados de <exame> no <nível> <destino>". */
  destino?: { nivel: NivelDeAplicacao; nome: string };
  /** Onde o destino fica, abaixo do título: "MetalSP › Soldador". */
  caminho?: string;
}

interface FormValues {
  periodicityMonths: number | null;
  isRequired: boolean;
  observations: string;
}

// ============================================================================
// VALIDATION SCHEMA
// ============================================================================

/** O mesmo limite do servidor. */
const LIMITE_DAS_OBSERVACOES = 2000;

const validationSchema: yup.ObjectSchema<FormValues> = yup.object({
  periodicityMonths: yup
    .number()
    .nullable()
    .defined()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value
    )
    .min(1, 'Periodicidade deve ser pelo menos 1 mês')
    .max(120, 'Periodicidade não pode exceder 120 meses (10 anos)'),
  isRequired: yup.boolean().required().defined(),
  observations: yup
    .string()
    .defined()
    .max(LIMITE_DAS_OBSERVACOES, `Observações não podem exceder ${LIMITE_DAS_OBSERVACOES} caracteres`),
});

/** A periodicidade padrão do catálogo, em meses, quando o exame a traz. */
function mesesDoCatalogo(exam: AvailableExam | ExamWithApplication | null): number | null {
  if (!exam || !('defaultFrequency' in exam)) return null;
  return parseFrequencyToMonths(exam.defaultFrequency);
}

// ============================================================================
// COMPONENT
// ============================================================================

export const ExamApplicationModal: React.FC<ExamApplicationModalProps> = ({
  open,
  onClose,
  onConfirm,
  exam,
  mode,
  loading = false,
  error = null,
  destino,
  caminho,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      periodicityMonths: null,
      isRequired: true,
      observations: '',
    },
  });

  // Reset form when modal opens/closes or exam changes
  useEffect(() => {
    if (open && exam) {
      if (isExamWithApplication(exam)) {
        // Editing existing application
        reset({
          periodicityMonths: exam.periodicityMonths ?? null,
          isRequired: exam.isRequired,
          observations: exam.observations ?? '',
        });
      } else {
        // New application — pré-preenche a periodicidade a partir da
        // frequência padrão do catálogo do exame (#266), editável pelo usuário.
        reset({
          periodicityMonths: parseFrequencyToMonths(exam.defaultFrequency),
          isRequired: true,
          observations: '',
        });
      }
    }
  }, [open, exam, reset]);

  const onSubmit = async (data: FormValues) => {
    const applicationData: ApplicationData = {
      periodicityMonths: data.periodicityMonths,
      isRequired: data.isRequired,
      observations: data.observations ?? null,
    };

    await onConfirm(applicationData);
  };

  const examName = exam?.examName ?? 'Exame';
  const ocupado = loading || isSubmitting;
  const obrigatorio = useWatch({ control, name: 'isRequired' });
  const periodicidade = useWatch({ control, name: 'periodicityMonths' });
  const sugestao = mesesDoCatalogo(exam);

  // Só o exame obrigatório cobra periodicidade: sem ela ele não entra na convocação.
  const semPeriodicidade = obrigatorio && !periodicidade;
  const completude = { preenchidas: semPeriodicidade ? 0 : 1, total: 1, faltam: semPeriodicidade ? ['Periodicidade'] : [] };

  let titulo = mode === 'apply' ? 'Aplicar Exame' : 'Editar Aplicação';
  if (destino) titulo = `Dados de ${examName} ${referencia('em', destino.nivel, destino.nome)}`;

  // Fora do obrigatório a periodicidade fica esmaecida, e não travada: dá para ajustá-la antes de marcar.
  let ajudaDaPeriodicidade = 'Intervalo em meses entre exames';
  if (!obrigatorio) ajudaDaPeriodicidade = 'Não se aplica enquanto o exame não for obrigatório';
  else if (semPeriodicidade) ajudaDaPeriodicidade = 'Falta: sem a periodicidade o exame não entra na convocação';

  return (
    <Dialog
      open={open}
      onClose={ocupado ? undefined : onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { maxWidth: 760 } }}
    >
      <DialogTitle>
        {titulo}
        <Typography variant="body2" color="text.secondary" component="span" display="block" sx={{ mt: 0.5 }}>
          {destino ? caminho : examName}
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Stack spacing={3}>
            {error && <Alert severity="error">{error}</Alert>}

            {obrigatorio ? (
              <Box>
                <BarraDeCompletude completude={completude} comContagem />
                {semPeriodicidade && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Falta: </strong>
                    <span>Periodicidade — sem ela o exame não entra na convocação</span>
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Completude: não se aplica
              </Typography>
            )}

            <Controller
              name="isRequired"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      disabled={ocupado}
                    />
                  }
                  label={
                    <Box component="span" sx={{ display: 'block' }}>
                      <Typography variant="body1" component="span" display="block">
                        Exame obrigatório
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="span" display="block">
                        {field.value ? 'Entra na convocação e no PCMSO' : 'Não entra na convocação'}
                      </Typography>
                    </Box>
                  }
                />
              )}
            />

            <Box>
              <Controller
                name="periodicityMonths"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === '' ? null : parseInt(value, 10));
                    }}
                    label="Periodicidade"
                    type="number"
                    sx={{ maxWidth: 240, opacity: obrigatorio ? 1 : 0.6 }}
                    fullWidth
                    error={!!errors.periodicityMonths}
                    helperText={errors.periodicityMonths?.message || ajudaDaPeriodicidade}
                    disabled={ocupado}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">meses</InputAdornment>
                      ),
                      inputProps: { min: 1, max: 120 },
                    }}
                  />
                )}
              />
              {obrigatorio && sugestao !== null && periodicidade !== sugestao && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {`Catálogo sugere ${sugestao} meses · `}
                  <AcaoEmLink
                    aria-label={`Usar ${sugestao} meses`}
                    disabled={ocupado}
                    onClick={() => setValue('periodicityMonths', sugestao, { shouldValidate: true })}
                  >
                    Usar
                  </AcaoEmLink>
                </Typography>
              )}
            </Box>

            <Controller
              name="observations"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Observações"
                  multiline
                  rows={3}
                  fullWidth
                  error={!!errors.observations}
                  helperText={
                    <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <span>{errors.observations?.message ?? `Opcional · até ${LIMITE_DAS_OBSERVACOES} caracteres`}</span>
                      <span>{`${field.value?.length ?? 0} / ${LIMITE_DAS_OBSERVACOES}`}</span>
                    </Box>
                  }
                  disabled={ocupado}
                />
              )}
            />

            <Alert severity="info">
              <strong>Dica:</strong> o vencimento é calculado por funcionário: último exame concluído (ou a admissão) mais a periodicidade.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
            Salva tudo de uma vez; dá para salvar faltando informação e voltar depois.
          </Typography>
          <Button onClick={onClose} disabled={ocupado} color="inherit">
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={ocupado} startIcon={ocupado && <CircularProgress size={16} />}>
            {mode === 'apply' ? 'Aplicar' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ExamApplicationModal;
